import { useCallback, useEffect, useState } from 'react';
import { getMyPreferences, updateMyPreferences } from '../api';
import {
  broadcastUserPreferencesUpdated,
  normalizeUserPreferences,
  normalizeUserPreferencesUpdate,
  subscribeUserPreferencesUpdates,
} from '../utils/userPreferences';

const EMPTY_PREFERENCES = normalizeUserPreferences(null);

const useMyPreferences = ({ enabled = true } = {}) => {
  const [preferences, setPreferences] = useState(EMPTY_PREFERENCES);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setError('');
      setPreferences(EMPTY_PREFERENCES);
      return EMPTY_PREFERENCES;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getMyPreferences();
      const normalized = normalizeUserPreferences(response);
      setPreferences(normalized);
      return normalized;
    } catch (requestError) {
      setError(requestError?.message || 'No se pudieron cargar las preferencias.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  useEffect(() => subscribeUserPreferencesUpdates((incoming) => {
    setPreferences((previous) => normalizeUserPreferences({
      ...previous,
      ...incoming,
    }));
  }), []);

  const savePreferences = useCallback(async (updates = {}) => {
    const normalizedUpdates = normalizeUserPreferencesUpdate(updates);
    if (!Object.keys(normalizedUpdates).length) {
      return preferences;
    }

    const response = await updateMyPreferences(normalizedUpdates);
    const normalized = normalizeUserPreferences(response);
    setPreferences(normalized);
    broadcastUserPreferencesUpdated(normalized);
    return normalized;
  }, [preferences]);

  return {
    preferences,
    loading,
    error,
    refresh,
    savePreferences,
    setPreferences,
  };
};

export default useMyPreferences;
